import React, { FC, useEffect, useId, useState } from "react";

import { FormChipsInput, ActionableItem, SelectableItem } from "stentor-models";

import { Styleable } from "../../types";

import { FormFieldProps } from "../FormDisplay";
import { CheckIcon } from "../icons";

import styles from "./FormFieldChips.module.scss";

export function isSelectableItem(item: ActionableItem | SelectableItem): item is SelectableItem {
    return !!item && !(item as ActionableItem).url;
}

export function isActionableItem(item: ActionableItem | SelectableItem): item is ActionableItem {
    return !!(item as ActionableItem).url;
}

export interface FormFieldChipsProps extends FormFieldProps {
    readonly field: Styleable<FormChipsInput>;
    /** Optional prefilled value that takes precedence over items' selected state.
     *  Only used for initial mount; late-arriving updates (after mount) are not supported. */
    readonly value?: string[];
}

export const FormFieldChips: FC<FormFieldChipsProps> = (props) => {
    const { field, onFieldChange, errorMessage, value } = props;

    /**
     * Selected chips
     */
    // Use prefilled value if provided, otherwise look for pre-selected items
    const selectedFromProps = value || props.field.items
        .filter(isSelectableItem) // filter out actionable items
        .filter((item) => item.selected)
        .map((item) => item.id);
    const [selectedChips, setSelectedChips] = useState<string[]>(selectedFromProps); // array!

    const handleChipClick = (id: string) => {
        const checked = selectedChips.includes(id);

        if (field.radio) {
            setSelectedChips((_prevOptions) => {
                if (!checked) {
                    return [id]; // add
                } else {
                    return []; // delete
                }
            });
        } else {
            setSelectedChips((prevOptions) => {
                if (!checked) {
                    return [...prevOptions, id]; // add
                } else {
                    return prevOptions.filter((option) => option !== id); // delete
                }
            });
        }
    };

    const id = `xapp-chips-${useId()}`;
    const titleId = `${id}-title`;
    const helperId = `${id}-helper-text`;

    useEffect(() => {
        if (typeof onFieldChange === "function") {
            onFieldChange(field.name, selectedChips);
        }
    }, [selectedChips, field.name, onFieldChange]);

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignContent: "center",
                alignItems: "center",
            }}>
            <div
                className={`${styles.xapp_chips_title_container}${!field.title ? "__empty" : ""}`}
                style={{ ...field.style }}>
                <p id={titleId}>{field.title}</p>
            </div>

            <div
                className={styles.xapp_chips_container}
                role="group"
                aria-labelledby={field.title ? titleId : undefined}
                aria-describedby={errorMessage ? helperId : undefined}>
                {field.items.map((item, index) => {
                    const selected = selectedChips.includes(item.id);
                    const className = `${styles.xapp_checkbox_chip} ${selected ? styles.xapp_chip_clicked : ""}`;
                    const content = (
                        <>
                            {selected && <CheckIcon className={styles.xapp_chip_icon} />}
                            <span className={styles.xapp_chip_label}>{item.label}</span>
                        </>
                    );

                    if (isSelectableItem(item)) {
                        return (
                            <div key={index} className={styles.xapp_chip_wrapper}>
                                <button
                                    type="button"
                                    aria-pressed={selected}
                                    onClick={() => handleChipClick(item.id)}
                                    className={className}>
                                    {content}
                                </button>
                            </div>
                        );
                    } else if (isActionableItem(item)) {
                        return (
                            <div key={index} className={styles.xapp_chip_wrapper}>
                                <a href={item.url} className={`${className} ${styles.xapp_chip_link}`}>
                                    {content}
                                </a>
                            </div>
                        );
                    } else {
                        return <></>;
                    }
                })}
            </div>

            {errorMessage && (
                <p id={helperId} className={styles.xapp_chips_helper}>
                    {errorMessage}
                </p>
            )}
        </div>
    );
};

export default FormFieldChips;
